import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import AdminPageHeader from '../components/common/AdminPageHeader';
import ProcedureQueueSection from '../components/procedures/ProcedureQueueSection';
import { useTheme } from '../theme/ThemeContext';
import { usePermissions } from '../context/PermissionContext';
import useSittingStore from '../stores/sittingStore';
import { printToken } from '../utils/printToken';
import { ROUTES } from '../constants/routes';
import {
  listPaymentsForSitting,
  openProcedureReceiptPdf,
  getVisitBillingSummary,
} from '../services/api/patientPlanApi';
import { fetchClinicBranding } from '../services/api/visitApi';
import { listMyActionPending } from '../services/api/visitActionPendingApi';
import ActionPendingChip from '../components/visit/ActionPendingChip';
import { visitPaymentDueRupees } from '../utils/visitMoney';
import eventBus from '../services/realtime/EventBus';
import { REALTIME_EVENTS } from '../services/realtime/RealtimeSyncService';
import {
  INTENT_CATEGORY,
  INTENT_CATEGORY_PALETTE,
  INTENT_LABELS,
  INTENTS,
  ROLE_LABELS,
} from '../constants/visitActionPending';
import ReferralResolveDialog from '../components/visit/ReferralResolveDialog';
import CollectReferralPaymentDialog from '../components/visit/CollectReferralPaymentDialog';

// Default shape for the token-header branding fetch — kept as a module
// constant so it's a stable reference before the fetch resolves.
const DEFAULT_BRANDING = { name: 'Clinic', description: '', address: '', pincode: '', phone: '' };

/**
 * ActionPendingPage — receptionist/doctor "Action Required" hub.
 *
 * Merges two attention sources behind one page:
 *   - Awaiting-payment sittings (pre/post-procedure tabs).
 *   - Visits parked with this receptionist via the action-pending
 *     overlay (Action visits tab).
 *
 * Procedure tabs drive sitting-level payment collection; the Action
 * visits tab surfaces visits with intents like COLLECT_PAYMENT /
 * ASSIGN_BACK_TO_ME and links back to the visit detail (where the
 * receptionist's BundledPaymentSheet lives).
 */
export default function ActionPendingPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { hasPermission, role: sessionRole, memberId: sessionMemberId } = usePermissions();

  const {
    actionPendingSittings,
    fetchActionPendingSittings,
    collectPayment,
    isLoading,
    error,
  } = useSittingStore();

  const viewer = useMemo(
    () => ({ memberId: sessionMemberId || '', role: sessionRole || '' }),
    [sessionMemberId, sessionRole],
  );

  // Default to the "Action visits" tab so returned visits land at the
  // top of the receptionist's attention.
  const [activeTab, setActiveTab] = useState('action_visits');

  // Separate state for the action-visits feed (own load/error
  // lifecycle, distinct from sitting action-pending).
  const [actionVisits, setActionVisits] = useState([]);
  const [actionVisitsLoading, setActionVisitsLoading] = useState(false);
  const [actionVisitsError, setActionVisitsError] = useState(null);

  const reloadActionVisits = useCallback(async () => {
    setActionVisitsLoading(true);
    setActionVisitsError(null);
    try {
      const res = await listMyActionPending();
      setActionVisits(res?.visits || []);
    } catch (err) {
      setActionVisitsError(err?.response?.data?.detail || err.message);
    } finally {
      setActionVisitsLoading(false);
    }
  }, []);

  const reload = useCallback(() => {
    if (activeTab === 'procedures_before') fetchActionPendingSittings({ timing: 'before' });
    else if (activeTab === 'procedures_after') fetchActionPendingSittings({ timing: 'after' });
    else if (activeTab === 'action_visits') reloadActionVisits();
  }, [activeTab, fetchActionPendingSittings, reloadActionVisits]);

  useEffect(() => {
    reload();
  }, [reload]);

  // Phase 6.0 — live refresh of action-visits feed on overlay events.
  useEffect(() => {
    const offSet = eventBus.on(
      REALTIME_EVENTS.VISIT_ACTION_PENDING_SET, reloadActionVisits,
    );
    const offResolved = eventBus.on(
      REALTIME_EVENTS.VISIT_ACTION_PENDING_RESOLVED, reloadActionVisits,
    );
    const offBundled = eventBus.on(
      REALTIME_EVENTS.VISIT_BUNDLED_PAYMENT_COLLECTED, reloadActionVisits,
    );
    return () => { offSet?.(); offResolved?.(); offBundled?.(); };
  }, [reloadActionVisits]);

  const [prompt, setPrompt] = useState(null); // { sitting }
  // Phase 3.1 — Referral forward dialog state
  const [referralResolve, setReferralResolve] = useState(null);
  // Phase 3.3 — "Collect" only path (record payment, keep visit parked).
  const [referralCollect, setReferralCollect] = useState(null);

  // Token-update T1 — clinic branding for the procedure-payment token
  // header/footer. Single fetch on page load; not refetched per print.
  const [branding, setBranding] = useState(DEFAULT_BRANDING);
  useEffect(() => {
    fetchClinicBranding().then(setBranding).catch(() => {});
  }, []);

  const handleAction = useCallback(
    async ({ action, sitting }) => {
      if (action === 'collect_payment') {
        setPrompt({ sitting });
      } else if (action === 'open_visit' && sitting.visit_id) {
        // Phase 1c — let the receptionist jump into the visit panel from
        // the action-pending list too.
        navigate(`${ROUTES.ORG}#visit=${sitting.visit_id}`);
      } else if (action === 'receipt') {
        // Phase 1c — reprint a historical receipt from the payment list.
        const r = await listPaymentsForSitting(sitting.plan_id, sitting.sitting_id);
        if (r?.success) {
          const payments = r.data?.payments || [];
          if (payments.length > 0) {
            await openProcedureReceiptPdf(payments[payments.length - 1].payment_id);
          }
        }
      } else {
        // Payment page only surfaces pay action; other secondary
        // actions (cancel, reassign) are out of scope here — defer to
        // the ProcedureQueuePage.
      }
    },
    [navigate],
  );

  const submitPayment = async ({ amount, payment_mode, appointment_slot_date, appointment_slot_time }) => {
    if (!prompt) return;
    const { plan_id, sitting_id } = prompt.sitting;
    const body = { amount, payment_mode };
    if (appointment_slot_date) body.appointment_slot_date = appointment_slot_date;
    if (appointment_slot_time) body.appointment_slot_time = appointment_slot_time;
    try {
      const result = await collectPayment(plan_id, sitting_id, body);
      if (result?.success) {
        printToken({
          receipt_number: result.data?.receipt_number,
          // GST-DOCNO/A2-d (R-GD-10) — the sitting-collect response carries
          // the stamped document number alongside the bare int (§8 C-6).
          receipt_number_printed: result.data?.receipt_number_printed,
          queue_number: null,
          // GATE 1 ruling — sitting summaries carry `patient_id` (backend
          // SittingSummary, see treatment_plans.py `_sitting_row_to_summary`);
          // thread it through so printToken's resolveUhid can render the UHID
          // row. Row is omitted internally when patient_id is absent.
          // GST-DOCNO/R-GD-6 — the collect-payment response also carries the
          // real `patient_uhid` (U2); prefer it over the last-5 fallback.
          patient: {
            first_name: prompt.sitting.patient_name || '',
            patient_id: prompt.sitting.patient_id,
            uhid: result.data?.patient_uhid,
          },
          // bk-075 fix — source the real advising doctor from the sitting
          // (visit_doctor_name, same field SittingCard/ReceptionistTaskWidget
          // render as "Dr. {name}"); undefined when the sitting doesn't carry
          // one, rather than inventing a name or keeping the dead role check.
          doctor_name: prompt.sitting.visit_doctor_name || undefined,
          payment_mode,
          visit_time: null,
          visit_date: null,
          clinic_name: branding.name,
          clinic_description: branding.description,
          address: branding.address,
          pincode: branding.pincode,
          phone: branding.phone,
          procedures: [{
            name: prompt.sitting.procedure_name || 'Procedure',
            amount,
            receipt_number: result.data?.receipt_number,
          }],
        });
        // Phase 1c — also open the A5 PDF receipt in a new viewer.
        if (result.data?.payment_id) {
          openProcedureReceiptPdf(result.data.payment_id);
        }
      }
    } finally {
      setPrompt(null);
      reload();
    }
  };

  if (!hasPermission('COLLECT_PAYMENT')) {
    return <Navigate to="/access-denied" replace />;
  }

  const bg = isDark
    ? 'linear-gradient(135deg, #0E0E0F 0%, #1a1a2e 50%, #0E0E0F 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #eef2ff 50%, #f8fafc 100%)';

  return (
    <div style={{ background: bg, minHeight: '100vh' }}>
      <AdminPageHeader />

      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold">Action Required</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Visits and procedures waiting on you.
            </p>
          </div>
          <button
            type="button"
            onClick={reload}
            className="text-sm px-3 py-1.5 rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800"
            disabled={isLoading}
          >
            {isLoading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 border-b border-slate-200 dark:border-slate-700 flex-wrap">
          {[
            ['action_visits', `Action visits${actionVisits.length ? ` · ${actionVisits.length}` : ''}`],
            ['walkin', 'Walk-in consultations'],
            ['procedures_before', 'Procedures (pre-payment)'],
            ['procedures_after', 'Procedures (post-service)'],
            ['by_visit', 'By visit'],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-teal-600 text-teal-700 dark:text-teal-300 font-semibold'
                  : 'border-transparent text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="mb-4 text-sm text-red-800 bg-red-50 border border-red-200 rounded p-2">
            {error}
          </div>
        )}

        {activeTab === 'action_visits' && (
          <ActionVisitsList
            visits={actionVisits}
            loading={actionVisitsLoading}
            error={actionVisitsError}
            onOpenVisit={(visitId) => {
              navigate(`${ROUTES.ORG}#visit=${visitId}`);
            }}
            onResolveReferral={(visit) => setReferralResolve(visit)}
            onCollectReferral={(visit) => setReferralCollect(visit)}
          />
        )}

        {activeTab === 'walkin' && (
          <div className="text-sm text-slate-500 italic py-4">
            Consultation billing continues to live on each visit's{' '}
            <span className="font-medium">Billing</span> tab — this placeholder
            is intentional. Clinic-wide consultation revenue views land in a
            later phase.
          </div>
        )}

        {activeTab === 'procedures_before' && (
          <ProcedureQueueSection
            title="Pay before execution"
            subtitle="These sittings can't start until someone marks them paid."
            sittings={actionPendingSittings}
            viewer={viewer}
            onAction={handleAction}
            emptyLabel="Nothing waiting."
          />
        )}

        {activeTab === 'procedures_after' && (
          <ProcedureQueueSection
            title="Pay after service"
            subtitle="Execution is done — collect payment to close out the sitting."
            sittings={actionPendingSittings}
            viewer={viewer}
            onAction={handleAction}
            emptyLabel="Nothing waiting."
          />
        )}

        {activeTab === 'by_visit' && (
          <ByVisitBilling
            sittings={actionPendingSittings}
            onAction={handleAction}
            isDark={isDark}
          />
        )}

        {prompt && (
          <CollectPaymentDialog
            sitting={prompt.sitting}
            onSubmit={submitPayment}
            onCancel={() => setPrompt(null)}
          />
        )}

        {referralResolve && (
          <ReferralResolveDialog
            visit={referralResolve}
            onSubmitted={() => {
              setReferralResolve(null);
              reloadActionVisits();
            }}
            onCancel={() => setReferralResolve(null)}
          />
        )}

        {referralCollect && (
          <CollectReferralPaymentDialog
            visit={referralCollect}
            onSubmitted={() => {
              setReferralCollect(null);
              reloadActionVisits();
            }}
            onCancel={() => setReferralCollect(null)}
          />
        )}
      </main>
    </div>
  );
}


/**
 * ActionVisitsList — Phase 6.0 feed of visits parked with this
 * receptionist via the action-pending overlay. One card per visit.
 *
 * Cards link straight to the visit detail surface where the
 * receptionist can collect via `BundledPaymentSheet` and then resolve
 * the overlay (which also fires the doctor follow-up flag and pops
 * the visit back into the doctor's My Tasks lane).
 */
function ActionVisitsList({
  visits, loading, error, onOpenVisit, onResolveReferral, onCollectReferral,
}) {
  // Phase 3.1 — intent-category filter pills (All / Payment / Referral / Nurse / Other)
  const [filter, setFilter] = useState('ALL');

  const counts = useMemo(() => {
    const out = { ALL: 0, PAYMENT: 0, REFERRAL: 0, NURSE: 0, OTHER: 0 };
    for (const v of visits || []) {
      out.ALL += 1;
      const cat = INTENT_CATEGORY[v.action_pending_intent] || 'OTHER';
      out[cat] = (out[cat] || 0) + 1;
    }
    return out;
  }, [visits]);

  const filtered = useMemo(() => {
    if (filter === 'ALL') return visits || [];
    return (visits || []).filter(
      (v) => (INTENT_CATEGORY[v.action_pending_intent] || 'OTHER') === filter,
    );
  }, [visits, filter]);

  if (loading && (visits || []).length === 0) {
    return (
      <div className="text-sm italic text-slate-500 dark:text-slate-400 py-4">
        Loading…
      </div>
    );
  }
  if (error) {
    return (
      <div className="text-sm text-rose-700 dark:text-rose-300 py-2">
        {error}
      </div>
    );
  }

  const pills = [
    { id: 'ALL', label: 'All', icon: '·' },
    { id: 'PAYMENT', label: 'Payment', icon: INTENT_CATEGORY_PALETTE.PAYMENT.icon },
    { id: 'REFERRAL', label: 'Referral', icon: INTENT_CATEGORY_PALETTE.REFERRAL.icon },
    { id: 'NURSE', label: 'Nurse', icon: INTENT_CATEGORY_PALETTE.NURSE.icon },
    { id: 'OTHER', label: 'Other', icon: INTENT_CATEGORY_PALETTE.OTHER.icon },
  ];

  return (
    <>
      <div className="mb-3 flex items-center gap-1.5 flex-wrap">
        {pills.map((p) => {
          const active = filter === p.id;
          const count = counts[p.id] ?? 0;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => setFilter(p.id)}
              className={[
                'inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs border transition-colors',
                active
                  ? 'bg-slate-900 text-white border-slate-900 dark:bg-slate-100 dark:text-slate-900 dark:border-slate-100'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50 dark:bg-slate-800 dark:text-slate-200 dark:border-slate-600 dark:hover:bg-slate-700',
              ].join(' ')}
            >
              <span>{p.icon}</span>
              <span>{p.label}</span>
              <span className={[
                'ml-1 px-1.5 rounded-full text-[10px]',
                active ? 'bg-white/20' : 'bg-slate-200 dark:bg-slate-700',
              ].join(' ')}>{count}</span>
            </button>
          );
        })}
      </div>

      {filtered.length === 0 ? (
        <div className="text-sm italic text-slate-500 dark:text-slate-400 py-4">
          {filter === 'ALL'
            ? 'Nothing waiting on you. Returned visits will appear here.'
            : 'Nothing in this lane.'}
        </div>
      ) : (
        <ul className="space-y-2">
          {filtered.map((v) => {
            const category = INTENT_CATEGORY[v.action_pending_intent] || 'OTHER';
            const palette = INTENT_CATEGORY_PALETTE[category];
            const isReferral = v.action_pending_intent === INTENTS.REFERRAL_TO_DOCTOR;
            return (
              <li
                key={v.visit_id}
                className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                      {v.patient_name || v.visit_id}
                      {v.queue_number != null && (
                        <span className="ml-2 text-xs text-slate-500">
                          #{v.queue_number}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                      {/* Phase 3.1 — category chip */}
                      <span
                        className={[
                          'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-medium',
                          palette.bg, palette.border, palette.text,
                        ].join(' ')}
                      >
                        <span>{palette.icon}</span>
                        <span>{palette.label}</span>
                      </span>
                      <ActionPendingChip visit={v} />
                      {v.doctor_name && (
                        <span className="text-slate-600 dark:text-slate-300">
                          Sent by {ROLE_LABELS.DOCTOR} · {v.doctor_name}
                        </span>
                      )}
                      {v.action_pending_intent && !isReferral && (
                        <span className="text-slate-600 dark:text-slate-300">
                          {INTENT_LABELS[v.action_pending_intent] || v.action_pending_intent}
                        </span>
                      )}
                      {isReferral && (() => {
                        const dueRupees = visitPaymentDueRupees(v);
                        return (
                          <span className="text-orange-700 dark:text-orange-200 font-medium">
                            → Dr {v.action_pending_target_doctor_id || '?'}
                            {dueRupees > 0 && ` · ₹${dueRupees.toFixed(2)} due`}
                            {Number(v.action_pending_payment_collected_paise || 0) > 0 && (
                              <span className="ml-1 text-emerald-700 dark:text-emerald-300">
                                · ✓ ₹{(v.action_pending_payment_collected_paise / 100).toFixed(2)} collected
                              </span>
                            )}
                          </span>
                        );
                      })()}
                    </div>
                    {isReferral && v.action_pending_referral_reason && (
                      <div className="mt-1 text-xs italic text-slate-600 dark:text-slate-300">
                        “{v.action_pending_referral_reason}”
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
                    {/* Phase 3.3 — three-button matrix:
                        - Referral (unpaid)  → [Collect] [Collect & Forward]
                        - Referral (paid)    → [Forward]
                        - Plain payment      → [Open visit] (collect lives on the visit) */}
                    {isReferral && (() => {
                      // Compare in paise so the legacy `payment_collected_paise`
                      // (still paise) stays apples-to-apples with `payment_due`
                      // (now rupees) after Phase 3.5.
                      const duePaise = Math.round(visitPaymentDueRupees(v) * 100);
                      const collected = Number(
                        v.action_pending_payment_collected_paise || 0,
                      );
                      const fullyCollected = duePaise > 0 && collected >= duePaise;
                      const noPaymentNeeded = duePaise === 0;
                      return (
                        <>
                          {!fullyCollected && !noPaymentNeeded && onCollectReferral && (
                            <button
                              type="button"
                              onClick={() => onCollectReferral(v)}
                              className="px-3 py-1.5 text-xs font-medium rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                              Collect
                            </button>
                          )}
                          {onResolveReferral && (
                            <button
                              type="button"
                              onClick={() => onResolveReferral(v)}
                              className="px-3 py-1.5 text-xs font-medium rounded-md bg-orange-600 hover:bg-orange-700 text-white"
                            >
                              {fullyCollected || noPaymentNeeded
                                ? 'Forward →'
                                : 'Collect & Forward →'}
                            </button>
                          )}
                        </>
                      );
                    })()}
                    <button
                      type="button"
                      onClick={() => onOpenVisit?.(v.visit_id)}
                      className="px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-700"
                    >
                      Open visit →
                    </button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}


function ByVisitBilling({ sittings, onAction, isDark }) {
  // Group pending sittings by visit_id — this becomes our "visits with
  // something pending" universe. For each, fetch the visit's
  // billing-summary (consultation + procedures) for the unified total.
  const visitIds = useMemo(() => {
    const seen = new Set();
    const out = [];
    for (const s of sittings || []) {
      if (s.visit_id && !seen.has(s.visit_id)) {
        seen.add(s.visit_id);
        out.push(s.visit_id);
      }
    }
    return out;
  }, [sittings]);

  const [summaries, setSummaries] = useState({});
  const [loadingVisits, setLoadingVisits] = useState(new Set());

  useEffect(() => {
    // Fetch billing summary for any visit_id not yet cached.
    let cancelled = false;
    const missing = visitIds.filter((v) => !summaries[v]);
    if (missing.length === 0) return undefined;
    setLoadingVisits(new Set(missing));
    (async () => {
      const results = await Promise.all(
        missing.map((v) => getVisitBillingSummary(v)),
      );
      if (cancelled) return;
      const next = {};
      results.forEach((r, i) => {
        if (r?.success) next[missing[i]] = r.data;
      });
      setSummaries((prev) => ({ ...prev, ...next }));
      setLoadingVisits(new Set());
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitIds.join(',')]);

  if (visitIds.length === 0) {
    return (
      <div className="text-sm italic text-slate-500 dark:text-slate-400 py-4">
        No visits with pending payment.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
        Each row shows the total due per visit — consultation + procedures
        (voided payments excluded). Click to expand line items.
      </p>
      {visitIds.map((vid) => {
        const summary = summaries[vid];
        const loading = loadingVisits.has(vid) && !summary;
        return (
          <VisitBillingRow
            key={vid}
            visitId={vid}
            summary={summary}
            loading={loading}
            onAction={onAction}
            isDark={isDark}
          />
        );
      })}
    </div>
  );
}


function VisitBillingRow({ visitId, summary, loading, onAction, isDark }) {
  const [expanded, setExpanded] = useState(false);
  if (loading) {
    return (
      <div className="rounded-md border border-slate-200 dark:border-slate-700 p-3 text-sm text-slate-400">
        Loading visit {visitId}…
      </div>
    );
  }
  if (!summary) return null;
  const total = Number(summary.total || 0);
  const paid = Number(summary.paid || 0);
  const due = Number(summary.due || 0);

  return (
    <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-3 flex items-center justify-between"
      >
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-slate-900 dark:text-slate-100 truncate">
            {summary.patient_name || 'Patient'}
            {summary.queue_number ? (
              <span className="ml-2 text-xs text-slate-500">#{summary.queue_number}</span>
            ) : null}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {summary.doctor_name ? `Dr. ${summary.doctor_name}` : ''}
            {summary.patient_mobile ? ` · ${summary.patient_mobile}` : ''}
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm font-semibold">₹{total.toFixed(0)}</div>
          <div className="text-xs text-slate-500">
            Paid ₹{paid.toFixed(0)} · Due ₹{due.toFixed(0)}
          </div>
        </div>
        <span className="ml-3 text-slate-400">{expanded ? '▲' : '▼'}</span>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 dark:border-slate-700 p-3 text-sm">
          <div className="flex justify-between py-1 text-slate-600 dark:text-slate-400">
            <span>Consultation</span>
            <span>₹{Number(summary.consultation_fee || 0).toFixed(0)}</span>
          </div>
          {Number(summary.registration_fee || 0) > 0 && (
            <div className="flex justify-between py-1 text-slate-600 dark:text-slate-400">
              <span>Registration</span>
              <span>₹{Number(summary.registration_fee || 0).toFixed(0)}</span>
            </div>
          )}
          {(summary.line_items || []).map((li) => (
            <div
              key={li.payment_id}
              className={`flex justify-between py-1 ${
                li.voided ? 'line-through text-slate-400' : 'text-slate-700 dark:text-slate-200'
              }`}
            >
              <span>
                {li.procedure_name}
                {li.voided ? ' (voided)' : ''}
                {li.receipt_number ? (
                  <span className="ml-2 text-xs text-slate-500">#{li.receipt_number}</span>
                ) : null}
              </span>
              <span>₹{Number(li.amount || 0).toFixed(0)}</span>
            </div>
          ))}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => onAction?.({
                action: 'open_visit',
                sitting: { visit_id: visitId },
              })}
              className="px-3 py-1 text-xs rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700"
            >
              Open visit →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function CollectPaymentDialog({ sitting, onSubmit, onCancel }) {
  const [amount, setAmount] = useState(sitting.final_cost?.toString() || '');
  // No payment-mode default — receptionist must pick UPI / CASH / CARD.
  const [mode, setMode] = useState('');
  const [includeSlot, setIncludeSlot] = useState(false);
  const [slotDate, setSlotDate] = useState(
    (sitting.scheduled_for || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
  );
  const [slotTime, setSlotTime] = useState('10:00');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState('');

  const isFuture = Boolean(
    sitting.scheduled_for && sitting.scheduled_for.slice(0, 10) > new Date().toISOString().slice(0, 10),
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!mode) {
      setLocalError('Please select a payment method.');
      return;
    }
    setLocalError('');
    setBusy(true);
    try {
      await onSubmit({
        amount,
        payment_mode: mode,
        appointment_slot_date: isFuture && includeSlot ? slotDate : undefined,
        appointment_slot_time: isFuture && includeSlot ? slotTime : undefined,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-lg shadow-xl p-5"
      >
        <h2 className="text-lg font-semibold mb-3">Collect payment</h2>

        <div className="mb-3">
          <label className="block text-sm font-medium mb-1">Amount (₹)</label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
            min="0"
            step="1"
            required
          />
        </div>

        <div className="mb-4">
          <label className="block text-sm font-medium mb-1">Mode</label>
          <div className="flex gap-2">
            {['UPI', 'CASH', 'CARD'].map((m) => (
              <button
                type="button"
                key={m}
                onClick={() => { setMode(m); setLocalError(''); }}
                className={`px-3 py-1.5 rounded-full text-sm border ${
                  mode === m ? 'bg-teal-600 text-white border-teal-600' : 'border-slate-300'
                }`}
              >
                {m}
              </button>
            ))}
          </div>
          {localError && (
            <p className="mt-1 text-xs text-rose-600 dark:text-rose-300">
              {localError}
            </p>
          )}
        </div>

        {isFuture && (
          <div className="mb-4 p-3 border border-sky-200 bg-sky-50 rounded">
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={includeSlot}
                onChange={(e) => setIncludeSlot(e.target.checked)}
              />
              Book an appointment slot now
            </label>
            {includeSlot && (
              <div className="mt-2 grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={slotDate}
                  onChange={(e) => setSlotDate(e.target.value)}
                  className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
                <input
                  type="time"
                  value={slotTime}
                  onChange={(e) => setSlotTime(e.target.value)}
                  className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
                />
              </div>
            )}
            <p className="mt-1 text-xs text-sky-800">
              Leaves sitting in 'scheduled' state until patient returns —
              banner will surface on registration if slot skipped.
            </p>
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !mode}
            className="px-4 py-1.5 text-sm rounded-md bg-teal-600 hover:bg-teal-700 text-white disabled:bg-teal-300"
          >
            {busy ? 'Submitting…' : 'Collect'}
          </button>
        </div>
      </form>
    </div>
  );
}
